import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Tooltip } from './Tooltip';

export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  /** Required accessible name (also shown as a tooltip). */
  label: string;
  icon: ReactNode;
  variant?: 'ghost' | 'secondary' | 'primary';
  /** Show the label as a tooltip on hover/focus (default true). */
  showTooltip?: boolean;
  /** Optional badge (e.g. unread count) rendered on the icon. */
  badge?: ReactNode;
}

const variants = {
  ghost: 'text-text hover:bg-surface-muted',
  secondary: 'border-2 border-border-strong bg-surface text-text hover:bg-surface-muted',
  primary: 'bg-primary text-on-primary hover:bg-primary-hover',
};

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  showTooltip = true,
  badge,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      type={type}
      aria-label={label}
      className={cn(
        'relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-60',
        variants[variant],
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
      {badge}
    </button>
  );
  return showTooltip ? <Tooltip content={label}>{button}</Tooltip> : button;
}
